package com.dotdrives.web.ui

import com.dotdrives.design.DotColors
import com.dotdrives.design.DotRadii
import com.dotdrives.design.DotShadows
import com.dotdrives.design.DotSpacing
import com.dotdrives.design.DotType
import react.FC
import react.PropsWithChildren
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.style
import web.cssom.ClassName

private val globalStyles = """
    :root {
      --dot-action: #B8491A;
      --dot-action-hover: #963B15;
      --dot-action-text: #A64114;
      --dot-muted: #5F6C75;
      --dot-positive: #007942;
      --dot-focus: #2B6CB8;
      color: ${DotColors.INK};
      background: ${DotColors.BACKGROUND};
      font-family: '${DotType.UI_FONT}', sans-serif;
      font-synthesis: none;
      text-rendering: optimizeLegibility;
    }
    * { box-sizing: border-box; }
    html { min-width: 320px; background: ${DotColors.BACKGROUND}; }
    body { min-width: 320px; min-height: 100vh; margin: 0; background: ${DotColors.BACKGROUND}; }
    button, input, textarea, select { font: inherit; }
    button { border: 0; }
    button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible {
      outline: 2px solid var(--dot-focus); outline-offset: 2px;
    }
    .dot-surface { cursor: pointer; }
    .dot-surface:hover, .dot-surface:focus-within { box-shadow: inset 0 0 0 2px var(--dot-focus); }
    .dot-home-link { display: block; width: fit-content; color: inherit; text-decoration: none; }
    .dot-theme { min-height: 100vh; background: ${DotColors.BACKGROUND}; color: ${DotColors.INK}; }
    .dot-heading { margin: 0; font-family: '${DotType.HEADING_FONT}', sans-serif; font-weight: ${DotType.HEADING_WEIGHT}; }
    .dot-h1 { font-size: ${DotType.H1}px; line-height: 34px; }
    .dot-h2 { font-size: ${DotType.H2}px; line-height: 28px; }
    .dot-h3 { font-size: ${DotType.H3}px; line-height: 24px; }
    .dot-body { margin: 0; font-size: ${DotType.BODY}px; line-height: 22px; font-weight: ${DotType.BODY_WEIGHT}; }
    .dot-label { font-size: ${DotType.LABEL}px; line-height: 18px; font-weight: ${DotType.LABEL_WEIGHT}; }
    .dot-meta {
      font-family: '${DotType.MONO_FONT}', monospace;
      font-size: ${DotType.MONO}px;
      line-height: 18px;
      font-weight: ${DotType.MONO_WEIGHT};
    }
    .dot-button {
      min-height: 46px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: ${DotSpacing.SM}px;
      padding: ${DotSpacing.MD}px ${DotSpacing.XL}px;
      border-radius: ${DotRadii.PILL}px;
      color: white;
      cursor: pointer;
      font-size: ${DotType.LABEL}px;
      line-height: 18px;
      font-weight: ${DotType.BUTTON_WEIGHT};
      transition: background-color 140ms ease, opacity 140ms ease, transform 140ms ease;
    }
    .dot-button-primary { background: var(--dot-action); }
    .dot-button-primary:hover:not(:disabled) { background: var(--dot-action-hover); }
    .dot-button-secondary {
      background: white;
      color: ${DotColors.INK};
      border: 1.5px solid ${DotColors.BORDER};
    }
    .dot-button-secondary:hover:not(:disabled) { background: ${DotColors.BACKGROUND}; }
    .dot-button-tinted { background: ${DotColors.PRIMARY_TINT}; color: var(--dot-action-text); }
    .dot-button-tinted:hover:not(:disabled) { background: ${DotColors.BORDER}; }
    .dot-button-ink { background: ${DotColors.INK}; color: white; }
    .dot-button-ink:hover:not(:disabled) { background: #31414A; }
    .dot-button:disabled { cursor: not-allowed; opacity: .48; }
    .dot-button:focus-visible, .dot-card-clickable:focus-visible, .dot-list-item-clickable:focus-visible,
    .dot-banner-retry:focus-visible {
      outline: 2px solid var(--dot-focus);
      outline-offset: 2px;
    }
    .dot-spinner {
      width: 18px;
      height: 18px;
      border: 2px solid currentColor;
      border-right-color: transparent;
      border-radius: 50%;
      animation: dot-spin .7s linear infinite;
    }
    .dot-field-wrap { display: flex; flex-direction: column; width: 100%; }
    .dot-field-label { margin-bottom: ${DotSpacing.SM}px; color: ${DotColors.INK}; }
    .dot-field {
      width: 100%;
      min-height: 46px;
      padding: ${DotSpacing.MD}px ${DotSpacing.LG}px;
      border: 1px solid ${DotColors.BORDER};
      border-radius: ${DotSpacing.MD}px;
      outline: none;
      color: ${DotColors.INK};
      background: ${DotColors.BACKGROUND};
      font-size: ${DotType.BODY}px;
      line-height: 22px;
    }
    textarea.dot-field { min-height: 104px; resize: vertical; }
    .dot-field::placeholder { color: var(--dot-muted); }
    .dot-field:focus { border: 2px solid ${DotColors.PRIMARY}; padding: ${DotSpacing.MD - 1}px ${DotSpacing.LG - 1}px; }
    .dot-field-error { border-color: ${DotColors.DANGER}; }
    .dot-field.dot-field-error:focus {
      border-color: ${DotColors.DANGER};
      box-shadow: 0 0 0 2px ${DotColors.DANGER_TINT};
    }
    .dot-field-message { margin-top: ${DotSpacing.XS}px; color: ${DotColors.DANGER}; font-family: '${DotType.UI_FONT}', sans-serif; }
    .dot-card {
      padding: ${DotSpacing.LG}px;
      border: 1px solid ${DotColors.BORDER};
      border-radius: ${DotRadii.CARD}px;
      background: white;
      box-shadow: ${DotShadows.CARD};
      transition: transform 160ms ease, box-shadow 160ms ease;
    }
    .dot-card:hover { transform: translateY(-2px); box-shadow: ${DotShadows.CARD}; }
    .dot-card-clickable {
      width: 100%;
      text-align: left;
      color: inherit;
      cursor: pointer;
    }
    .dot-pill {
      min-height: 26px;
      width: fit-content;
      display: inline-flex;
      align-items: center;
      padding: ${DotSpacing.XS}px ${DotSpacing.MD}px;
      border-radius: ${DotRadii.PILL}px;
      white-space: nowrap;
      font-size: ${DotType.LABEL}px;
      line-height: 18px;
      font-weight: ${DotType.BUTTON_WEIGHT};
    }
    .dot-tone-neutral { --dot-tone-accent: ${DotColors.BORDER}; color: ${DotColors.TEXT_SECONDARY}; background: ${DotColors.BACKGROUND}; }
    .dot-tone-primary { --dot-tone-accent: ${DotColors.PRIMARY}; color: var(--dot-action-text); background: ${DotColors.PRIMARY_TINT}; }
    .dot-tone-success { --dot-tone-accent: ${DotColors.SUCCESS}; color: ${DotColors.SUCCESS_TEXT}; background: ${DotColors.SUCCESS_TINT}; }
    .dot-tone-info { --dot-tone-accent: ${DotColors.INFO}; color: ${DotColors.INFO_TEXT}; background: ${DotColors.INFO_TINT}; }
    .dot-tone-danger { --dot-tone-accent: ${DotColors.DANGER}; color: ${DotColors.DANGER}; background: ${DotColors.DANGER_TINT}; }
    .dot-tone-automation { --dot-tone-accent: ${DotColors.AUTOMATION}; color: ${DotColors.AUTOMATION}; background: ${DotColors.AUTOMATION_TINT}; }
    .dot-tone-warning { --dot-tone-accent: ${DotColors.WARNING_TEXT}; color: ${DotColors.WARNING_TEXT}; background: ${DotColors.WARNING_TINT}; }
    .dot-avatar {
      display: inline-flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      color: var(--dot-action-text);
      background: ${DotColors.PRIMARY_TINT};
      font-family: '${DotType.HEADING_FONT}', sans-serif;
      font-weight: ${DotType.HEADING_WEIGHT};
    }
    .dot-list-item {
      width: 100%;
      display: flex;
      align-items: center;
      padding: ${DotSpacing.MD}px;
      border: 1px solid ${DotColors.BORDER};
      border-radius: ${DotRadii.CARD}px;
      color: inherit;
      background: white;
      text-align: left;
    }
    .dot-list-item-clickable { cursor: pointer; transition: border-color 140ms ease, background-color 140ms ease; }
    .dot-list-item-clickable:hover { border-color: ${DotColors.PRIMARY}; background: ${DotColors.PRIMARY_TINT}; }
    .dot-list-leading { display: flex; margin-right: ${DotSpacing.MD}px; }
    .dot-list-copy { min-width: 0; flex: 1; }
    .dot-list-subtitle { margin-top: ${DotSpacing.XS}px; color: var(--dot-muted); }
    .dot-list-trailing { display: flex; flex: 0 0 auto; margin-left: ${DotSpacing.MD}px; }
    .dot-empty-state { padding: ${DotSpacing.XL}px; text-align: center; }
    .dot-empty-emoji { font-size: ${DotType.H1 + DotSpacing.MD}px; line-height: 1; }
    .dot-empty-title { margin-top: ${DotSpacing.MD}px; }
    .dot-empty-body { max-width: 480px; margin: ${DotSpacing.SM}px auto 0; color: ${DotColors.TEXT_SECONDARY}; }
    .dot-empty-actions { max-width: 360px; display: grid; gap: ${DotSpacing.SM}px; margin: ${DotSpacing.XL}px auto 0; }
    .dot-full-width { width: 100%; }
    .dot-skeleton {
      overflow: hidden;
      position: relative;
      border-radius: ${DotSpacing.SM}px;
      background: ${DotColors.BACKGROUND};
    }
    .dot-skeleton::after {
      content: '';
      position: absolute;
      inset: 0;
      transform: translateX(-100%);
      background: linear-gradient(100deg, transparent 20%, rgba(255,255,255,.82) 50%, transparent 80%);
      animation: dot-shimmer 1.1s linear infinite;
    }
    .dot-banner {
      width: 100%;
      display: flex;
      align-items: center;
      gap: ${DotSpacing.MD}px;
      padding: ${DotSpacing.MD}px;
      border: 1px solid;
      border-color: color-mix(in srgb, var(--dot-tone-accent) 28%, transparent);
      border-radius: ${DotSpacing.MD}px;
    }
    .dot-banner-dot { width: ${DotSpacing.SM}px; height: ${DotSpacing.SM}px; flex: 0 0 auto; border-radius: 50%; }
    .dot-tone-neutral .dot-banner-dot, .dot-banner.dot-tone-neutral .dot-banner-dot { background: ${DotColors.BORDER}; }
    .dot-tone-primary .dot-banner-dot, .dot-banner.dot-tone-primary .dot-banner-dot { background: ${DotColors.PRIMARY}; }
    .dot-tone-success .dot-banner-dot, .dot-banner.dot-tone-success .dot-banner-dot { background: ${DotColors.SUCCESS}; }
    .dot-tone-info .dot-banner-dot, .dot-banner.dot-tone-info .dot-banner-dot { background: ${DotColors.INFO}; }
    .dot-tone-danger .dot-banner-dot, .dot-banner.dot-tone-danger .dot-banner-dot { background: ${DotColors.DANGER}; }
    .dot-tone-automation .dot-banner-dot, .dot-banner.dot-tone-automation .dot-banner-dot { background: ${DotColors.AUTOMATION}; }
    .dot-tone-warning .dot-banner-dot, .dot-banner.dot-tone-warning .dot-banner-dot { background: ${DotColors.WARNING_TEXT}; }
    .dot-banner-message { flex: 1; }
    .dot-banner-retry { padding: 2px 0; border: 0; color: inherit; background: transparent; cursor: pointer; font-weight: ${DotType.BUTTON_WEIGHT}; }
    .auth-state-page {
      min-height: 100vh; display: grid; place-items: center; padding: ${DotSpacing.XL}px;
      background: ${DotColors.BACKGROUND};
    }
    .auth-state-panel { width: min(100%, 620px); padding: ${DotSpacing.XXL}px; }
    .auth-state-panel:hover { transform: none; }
    .auth-state-title { margin-top: ${DotSpacing.SM}px; font-size: 30px; }
    .auth-state-copy { margin: ${DotSpacing.SM}px 0 ${DotSpacing.XL}px; color: ${DotColors.TEXT_SECONDARY}; }
    .auth-state-actions { display: flex; gap: ${DotSpacing.MD}px; margin-top: ${DotSpacing.LG}px; }
    .auth-state-signout { margin-top: ${DotSpacing.XL}px; }
    .org-picker-list { display: grid; gap: ${DotSpacing.MD}px; }
    .org-picker-card { width: 100%; padding: ${DotSpacing.LG}px; text-align: left; }
    .org-picker-name { color: ${DotColors.INK}; font-family: '${DotType.HEADING_FONT}', sans-serif; font-size: 18px; font-weight: 700; }
    .org-picker-meta { margin-top: ${DotSpacing.XS}px; color: var(--dot-muted); font-size: 12px; }
    .brand-splash {
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: white;
      background: ${DotColors.INK};
    }
    .brand-splash-mark { font-size: 28px; line-height: 20px; }
    .brand-splash-title { margin-top: ${DotSpacing.SM}px; font-size: 32px; }
    .brand-splash-copy { margin-top: ${DotSpacing.SM}px; color: #AAB6BD; letter-spacing: 1.2px; }
    .onboarding-placeholder {
      min-height: 100vh;
      display: grid;
      place-items: center;
      padding: ${DotSpacing.XL}px;
    }
    .onboarding-page {
      position: relative;
      min-height: 100vh;
      display: flex;
      overflow: hidden;
      color: white;
      background: ${DotColors.INK};
    }
    .onboarding-float-dot {
      position: absolute;
      z-index: 0;
      border-radius: 50%;
      pointer-events: none;
      animation: onboarding-float 7s ease-in-out infinite;
    }
    .onboarding-float-dot-orange {
      width: 14px;
      height: 14px;
      top: 9%;
      left: 7%;
      background: ${DotColors.PRIMARY};
      opacity: .62;
    }
    .onboarding-float-dot-blue {
      width: 10px;
      height: 10px;
      bottom: 13%;
      left: 17%;
      background: ${DotColors.INFO};
      opacity: .5;
      animation-duration: 8s;
    }
    .onboarding-float-dot-green {
      width: 12px;
      height: 12px;
      top: 22%;
      right: 8%;
      background: ${DotColors.SUCCESS};
      opacity: .48;
      animation-duration: 6s;
    }
    .onboarding-rail {
      position: relative;
      z-index: 1;
      width: min(36vw, 520px);
      min-width: 390px;
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      padding: 48px 56px;
    }
    .onboarding-brand {
      color: white;
      font-family: '${DotType.HEADING_FONT}', sans-serif;
      font-size: 20px;
      font-weight: ${DotType.HEADING_WEIGHT};
      letter-spacing: -.4px;
    }
    .onboarding-brand-mark {
      color: ${DotColors.PRIMARY};
      letter-spacing: -3px;
    }
    .onboarding-rail-content {
      margin: auto 0;
      padding: 48px 0;
    }
    .onboarding-rail-title {
      max-width: 390px;
      font-size: 38px;
      line-height: 1.15;
      letter-spacing: -1px;
    }
    .onboarding-rail-copy {
      max-width: 405px;
      margin: 18px 0 30px;
      color: #AEBBC3;
      font-size: 16px;
      line-height: 1.7;
    }
    .onboarding-progress-track {
      position: relative;
      height: 5px;
      margin: 0 8px 18px;
      overflow: visible;
      border-radius: 3px;
      background: rgba(255,255,255,.12);
    }
    .onboarding-progress-dot {
      position: absolute;
      top: -6px;
      left: 2%;
      width: 16px;
      height: 16px;
      border: 3px solid ${DotColors.INK};
      border-radius: 50%;
      background: ${DotColors.PRIMARY};
      box-shadow: 0 0 14px rgba(242,98,35,.8);
      animation: onboarding-travel 8s ease-in-out infinite;
    }
    .onboarding-step-list { display: flex; flex-direction: column; gap: 17px; }
    .onboarding-step-item {
      display: flex;
      align-items: center;
      gap: 14px;
      opacity: .55;
      transition: opacity 180ms ease;
    }
    .onboarding-step-item-active { opacity: 1; }
    .onboarding-step-item-complete { opacity: .76; }
    .onboarding-step-number {
      width: 34px;
      height: 34px;
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      border: 2px solid rgba(255,255,255,.25);
      border-radius: 50%;
      color: #8FA0AA;
      font-size: 14px;
      font-weight: ${DotType.BUTTON_WEIGHT};
    }
    .onboarding-step-item-active .onboarding-step-number {
      border-color: ${DotColors.PRIMARY};
      color: white;
      background: ${DotColors.PRIMARY};
    }
    .onboarding-step-item-complete .onboarding-step-number {
      border-color: ${DotColors.SUCCESS};
      color: white;
      background: ${DotColors.SUCCESS};
    }
    .onboarding-step-name { color: white; font-size: 15px; font-weight: ${DotType.BUTTON_WEIGHT}; }
    .onboarding-step-subtitle { margin-top: 1px; color: #8FA0AA; font-size: 12.5px; }
    .onboarding-stage {
      position: relative;
      z-index: 1;
      min-width: 0;
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 44px;
    }
    .onboarding-panel {
      width: min(100%, 540px);
      max-height: calc(100vh - 64px);
      overflow: auto;
      padding: 40px 44px;
      border-radius: 22px;
      color: ${DotColors.INK};
      background: white;
      box-shadow: 0 30px 80px rgba(0,0,0,.3);
      animation: onboarding-pop-in .45s ease both;
    }
    .onboarding-panel-finished {
      max-width: 480px;
      color: white;
      background: ${DotColors.INK};
      border: 1px solid rgba(255,255,255,.1);
    }
    .onboarding-step-form { display: flex; flex-direction: column; }
    .onboarding-step-meta {
      margin-bottom: 8px;
      color: ${DotColors.PRIMARY};
      font-size: 11px;
      letter-spacing: 2px;
    }
    .onboarding-step-title { font-size: 27px; line-height: 34px; }
    .onboarding-step-copy {
      margin: 6px 0 24px;
      color: var(--dot-muted);
      font-size: 14px;
      line-height: 20px;
    }
    .onboarding-step-form > .dot-banner { margin-bottom: ${DotSpacing.LG}px; }
    .onboarding-step-form > .dot-field-wrap { margin-bottom: 18px; }
    .onboarding-profile-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
      margin-bottom: 16px;
    }
    .onboarding-display-link {
      color: ${DotColors.PRIMARY};
      cursor: help;
      font-weight: ${DotType.BUTTON_WEIGHT};
    }
    .onboarding-field-group { margin-bottom: 18px; }
    .onboarding-field-label { margin-bottom: 7px; }
    .onboarding-chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
    .onboarding-chip {
      min-height: 38px;
      padding: 8px 16px;
      border: 2px solid ${DotColors.BORDER};
      border-radius: ${DotRadii.PILL}px;
      color: ${DotColors.TEXT_SECONDARY};
      background: white;
      cursor: pointer;
      font-size: ${DotType.LABEL}px;
      font-weight: ${DotType.BUTTON_WEIGHT};
    }
    .onboarding-chip:hover { border-color: ${DotColors.PRIMARY}; }
    .onboarding-chip:focus-visible, .onboarding-role-select:focus-visible,
    .onboarding-invite-email:focus-visible, .onboarding-add-invite:focus-visible,
    .onboarding-remove-invite:focus-visible {
      outline: 2px solid var(--dot-focus);
      outline-offset: 2px;
    }
    .onboarding-chip-selected {
      border-color: ${DotColors.PRIMARY};
      color: var(--dot-action-text);
      background: ${DotColors.PRIMARY_TINT};
    }
    .onboarding-process-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      border: 1px solid ${DotColors.BORDER};
      border-radius: 12px;
    }
    .onboarding-process-icon { font-size: 18px; }
    .onboarding-process-copy { min-width: 0; flex: 1; }
    .onboarding-process-title { font-size: 13.5px; font-weight: ${DotType.BUTTON_WEIGHT}; }
    .onboarding-process-subtitle { margin-top: 2px; color: var(--dot-muted); font-size: 11.5px; }
    .onboarding-process-check { color: ${DotColors.SUCCESS}; font-weight: ${DotType.UI_BLACK_WEIGHT}; }
    .onboarding-actions { display: flex; gap: 10px; margin-top: 8px; }
    .onboarding-actions > * { min-width: 0; flex: 1; }
    .onboarding-action-primary { flex: 2 !important; }
    .onboarding-actions-three { align-items: stretch; }
    .onboarding-invite-list { display: flex; flex-direction: column; gap: 9px; }
    .onboarding-invite-row {
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border: 1px solid ${DotColors.BORDER};
      border-radius: 12px;
      background: white;
    }
    .onboarding-invite-avatar { color: white; background: ${DotColors.INFO}; }
    .onboarding-invite-email {
      min-width: 80px;
      flex: 1;
      padding: 8px 4px;
      border: 0;
      outline: 0;
      color: ${DotColors.INK};
      background: transparent;
      font-size: 13px;
      font-weight: 700;
    }
    .onboarding-invite-email::placeholder { color: #B4BCC2; font-weight: 400; }
    .onboarding-role-select {
      max-width: 96px;
      padding: 7px 9px;
      border: 0;
      border-radius: ${DotRadii.PILL}px;
      color: ${DotColors.INFO_TEXT};
      background: ${DotColors.INFO_TINT};
      cursor: pointer;
      font-size: 11px;
      font-weight: ${DotType.BUTTON_WEIGHT};
    }
    .onboarding-remove-invite {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      color: var(--dot-muted);
      background: transparent;
      cursor: pointer;
      font-size: 19px;
    }
    .onboarding-remove-invite:hover { color: ${DotColors.DANGER}; background: ${DotColors.DANGER_TINT}; }
    .onboarding-add-invite {
      width: 100%;
      padding: 12px;
      border: 2px dashed #D6DBE0;
      border-radius: 12px;
      color: var(--dot-muted);
      background: transparent;
      cursor: pointer;
      font-size: 13px;
      font-weight: ${DotType.BUTTON_WEIGHT};
    }
    .onboarding-add-invite:hover { border-color: ${DotColors.PRIMARY}; color: var(--dot-action-text); background: #FEF8F6; }
    .onboarding-tip-card {
      display: flex;
      gap: 9px;
      margin-top: 16px;
      padding: 13px 15px;
      border: 1px solid #FBCEBA;
      border-radius: 12px;
      color: var(--dot-action-text);
      background: #FEF8F6;
      font-size: 12.5px;
      line-height: 1.5;
    }
    .onboarding-csv-disabled {
      position: relative;
      padding: 28px;
      border: 2px dashed ${DotColors.PRIMARY};
      border-radius: ${DotRadii.CARD}px;
      color: var(--dot-muted);
      background: #FEF8F6;
      text-align: center;
      opacity: .68;
    }
    .onboarding-coming-soon { position: absolute; top: 12px; right: 12px; }
    .onboarding-csv-icon { margin-bottom: 6px; font-size: 28px; filter: grayscale(.25); }
    .onboarding-csv-title { color: var(--dot-action-text); font-size: 14px; font-weight: ${DotType.BUTTON_WEIGHT}; }
    .onboarding-csv-copy { margin-top: 2px; font-size: 11.5px; }
    .onboarding-finished { padding: 4px 0; text-align: center; }
    .onboarding-finished-emoji { margin-bottom: 12px; font-size: 46px; }
    .onboarding-finished-title { font-size: 26px; line-height: 34px; }
    .onboarding-finished-copy {
      max-width: 380px;
      margin: 10px auto 24px;
      color: #AEBBC3;
      font-size: 13.5px;
      line-height: 1.65;
    }
    .onboarding-finished-action { min-width: 240px; }
    .portal-shell { min-height: 100vh; display: flex; background: ${DotColors.BACKGROUND}; }
    .portal-sidebar {
      width: 232px;
      min-height: 100vh;
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      padding: 22px 14px;
      color: #C9D3D9;
      background: ${DotColors.INK};
    }
    .portal-brand {
      display: flex;
      align-items: center;
      gap: 7px;
      margin: 3px 10px 27px;
      color: white;
      font-family: '${DotType.HEADING_FONT}', sans-serif;
      font-size: 19px;
      font-weight: ${DotType.HEADING_WEIGHT};
      letter-spacing: -.4px;
    }
    .portal-brand-mark { color: ${DotColors.PRIMARY}; letter-spacing: -3px; }
    .portal-nav { display: flex; flex-direction: column; gap: 4px; }
    .portal-nav-item {
      position: relative;
      width: 100%;
      min-height: 42px;
      display: flex;
      align-items: center;
      gap: ${DotSpacing.MD}px;
      padding: 10px 12px;
      border-radius: 10px;
      color: #C9D3D9;
      background: transparent;
      cursor: pointer;
      text-align: left;
      font-size: 14.5px;
      font-weight: 600;
      transition: color 140ms ease, background-color 140ms ease;
    }
    .portal-nav-item:hover { color: white; background: #2C3941; }
    .portal-nav-item-active {
      color: white;
      background: rgba(242,98,35,.17);
      font-weight: ${DotType.BUTTON_WEIGHT};
    }
    .portal-nav-item-active::before {
      content: '';
      position: absolute;
      left: 0;
      width: 3px;
      height: 22px;
      border-radius: 0 3px 3px 0;
      background: ${DotColors.PRIMARY};
    }
    .portal-nav-icon {
      width: 20px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: inherit;
      font-size: 16px;
    }
    .portal-user {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-top: auto;
      padding: 14px 6px 0;
      border-top: 1px solid rgba(255,255,255,.08);
    }
    .portal-user-avatar { color: white; background: var(--dot-action); }
    .portal-user-copy { min-width: 0; flex: 1; }
    .portal-user-name, .portal-user-org { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .portal-user-name { color: white; font-size: 13.5px; font-weight: ${DotType.BUTTON_WEIGHT}; }
    .portal-user-org { color: #8FA0AA; font-size: 11.5px; }
    .portal-sign-out {
      width: 28px;
      height: 28px;
      flex: 0 0 auto;
      border-radius: 50%;
      color: #8FA0AA;
      background: transparent;
      cursor: pointer;
      font-size: 16px;
    }
    .portal-sign-out:hover { color: white; background: rgba(255,255,255,.08); }
    .portal-workspace { min-width: 0; flex: 1; display: flex; flex-direction: column; }
    .portal-topbar {
      min-height: 70px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: ${DotSpacing.LG}px;
      padding: ${DotSpacing.LG}px ${DotSpacing.XL}px;
      border-bottom: 1px solid ${DotColors.BORDER};
      background: white;
    }
    .portal-topbar-actions { display: flex; align-items: center; gap: ${DotSpacing.SM}px; }
    .portal-content {
      min-height: 0;
      flex: 1;
      padding: ${DotSpacing.XL}px;
      background: ${DotColors.BACKGROUND};
    }
    .module-placeholder {
      min-height: calc(100vh - 118px);
      display: grid;
      place-items: center;
      border: 1px dashed #D6DBE0;
      border-radius: ${DotRadii.PANEL}px;
      background: rgba(255,255,255,.35);
    }
    .gallery-page { min-height: 100vh; }
    .gallery-header { background: ${DotColors.INK}; }
    .gallery-header-inner { max-width: 1040px; margin: 0 auto; padding: ${DotSpacing.XXL}px ${DotSpacing.XL}px; }
    .gallery-brand, .gallery-eyebrow { color: ${DotColors.PRIMARY}; letter-spacing: 1.2px; }
    .gallery-title { margin-top: ${DotSpacing.SM}px; color: white; }
    .gallery-header-copy { margin-top: ${DotSpacing.SM}px; color: #A4B1B9; }
    .gallery-main {
      max-width: 1040px;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: ${DotSpacing.LG}px;
      margin: 0 auto;
      padding: ${DotSpacing.LG}px ${DotSpacing.LG}px ${DotSpacing.XXL}px;
    }
    .gallery-section:first-child, .gallery-section:last-child { grid-column: 1 / -1; }
    .gallery-card { height: 100%; }
    .gallery-section-title { margin-top: ${DotSpacing.XS}px; }
    .gallery-section-content { margin-top: ${DotSpacing.LG}px; }
    .gallery-stack { display: grid; gap: ${DotSpacing.SM}px; }
    .gallery-stack-md { gap: ${DotSpacing.MD}px; }
    .gallery-stack-lg { gap: ${DotSpacing.LG}px; }
    .gallery-inline { display: flex; flex-wrap: wrap; gap: ${DotSpacing.SM}px; }
    .gallery-align-center { align-items: center; gap: ${DotSpacing.LG}px; }
    .gallery-row { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: ${DotSpacing.LG}px; }
    .gallery-copy { margin-top: ${DotSpacing.SM}px; color: ${DotColors.TEXT_SECONDARY}; }
    .gallery-muted { color: var(--dot-muted); }
    .gallery-arrow { color: ${DotColors.PRIMARY}; }
    .gallery-token-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: ${DotSpacing.SM}px ${DotSpacing.LG}px; }
    .gallery-swatch { min-width: 0; display: flex; align-items: center; gap: ${DotSpacing.MD}px; padding: ${DotSpacing.XS}px 0; }
    .gallery-swatch-color { width: 44px; height: 44px; flex: 0 0 auto; border: 1px solid ${DotColors.BORDER}; border-radius: ${DotSpacing.SM}px; }
    .gallery-swatch-copy { min-width: 0; overflow-wrap: anywhere; }
    .gallery-skeleton-row { display: flex; align-items: center; gap: ${DotSpacing.MD}px; }
    .gallery-skeleton-copy { min-width: 0; flex: 1; display: grid; gap: ${DotSpacing.SM}px; }
    @keyframes dot-spin { to { transform: rotate(360deg); } }
    @keyframes dot-shimmer { to { transform: translateX(100%); } }
    @keyframes onboarding-float { 50% { transform: translateY(-10px); } }
    @keyframes onboarding-pop-in {
      from { transform: translateY(14px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes onboarding-travel {
      0%, 100% { left: 2%; }
      30%, 36% { left: 32%; }
      66%, 72% { left: 64%; }
      98% { left: 95%; }
    }
    @media (max-width: 980px) {
      .onboarding-rail { width: 360px; min-width: 360px; padding: 36px 32px; }
      .onboarding-rail-title { font-size: 31px; }
      .onboarding-stage { padding: 28px; }
      .onboarding-panel { padding: 32px; }
    }
    @media (max-width: 760px) {
      .portal-sidebar { width: 76px; padding-inline: 10px; }
      .portal-brand { justify-content: center; margin-inline: 0; }
      .portal-brand-name, .portal-nav-item > span:last-child, .portal-user-copy, .portal-sign-out { display: none; }
      .portal-nav-item { justify-content: center; padding-inline: 8px; }
      .portal-user { justify-content: center; padding-inline: 0; }
      .portal-content { padding: ${DotSpacing.MD}px; }
      .portal-topbar { min-height: 62px; padding: ${DotSpacing.MD}px ${DotSpacing.LG}px; }
      .module-placeholder { min-height: calc(100vh - 86px); }
      .gallery-main { grid-template-columns: 1fr; padding: ${DotSpacing.LG}px ${DotSpacing.MD}px ${DotSpacing.XXL}px; }
      .gallery-section:first-child, .gallery-section:last-child { grid-column: auto; }
      .gallery-token-grid { grid-template-columns: 1fr; }
      .gallery-header-inner { padding: ${DotSpacing.XXL}px ${DotSpacing.LG}px; }
      .dot-list-item { align-items: flex-start; }
      .dot-list-trailing { margin-left: ${DotSpacing.SM}px; }
      .onboarding-page { display: block; overflow: visible; }
      .onboarding-rail {
        width: 100%;
        min-width: 0;
        padding: 24px 20px 20px;
      }
      .onboarding-rail-content { margin: 24px 0 0; padding: 0; }
      .onboarding-rail-title { max-width: none; font-size: 27px; }
      .onboarding-rail-copy { margin: 10px 0 20px; font-size: 14px; line-height: 1.5; }
      .onboarding-progress-track { display: none; }
      .onboarding-step-list { flex-direction: row; gap: 6px; }
      .onboarding-step-item { flex: 1; display: block; }
      .onboarding-step-number { width: 100%; height: 5px; border: 0; border-radius: 3px; color: transparent; background: rgba(255,255,255,.15); }
      .onboarding-step-item-active .onboarding-step-number,
      .onboarding-step-item-complete .onboarding-step-number { color: transparent; background: ${DotColors.PRIMARY}; }
      .onboarding-step-name, .onboarding-step-subtitle { display: none; }
      .onboarding-stage { display: block; padding: 0; }
      .onboarding-panel {
        width: 100%;
        max-height: none;
        min-height: calc(100vh - 244px);
        padding: 28px 20px 36px;
        border-radius: 20px 20px 0 0;
        box-shadow: none;
      }
      .onboarding-panel-finished { max-width: none; min-height: 420px; display: grid; place-items: center; }
      .onboarding-profile-grid { grid-template-columns: 1fr; margin-bottom: 0; }
      .onboarding-profile-grid > .dot-field-wrap { margin-bottom: 16px; }
      .onboarding-actions, .onboarding-actions-three { flex-wrap: wrap; }
      .onboarding-actions-three .onboarding-action-primary { order: -1; flex-basis: 100% !important; }
      .onboarding-invite-row { gap: 7px; padding-inline: 10px; }
      .onboarding-invite-avatar { display: none; }
      .onboarding-role-select { max-width: 84px; }
    }
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after { scroll-behavior: auto !important; transition-duration: .01ms !important; animation-duration: .01ms !important; animation-iteration-count: 1 !important; }
    }
""".trimIndent()

val DotTheme = FC<PropsWithChildren> { props ->
    style {
        +globalStyles
    }
    div {
        className = ClassName("dot-theme")
        +props.children
    }
}
